import React, { useCallback, type ReactNode } from "react";
import domToImage from "dom-to-image";
import Option from "@ui/Option";
import useToasts from "@ui/Toast/useToasts";
import t from "../../i18n";

interface ExportPDFOptionProps {
  children: ReactNode;
  className?: string;
}

const imageExportOptionsConfig = {
  bgcolor: "white",
  width: "100%",
  height: "auto",
  maxWidth: "28cm", // A4 width
  maxHeight: "19cm", // A4 height
  objectFit: "contain", // Maintain aspect ratio
};

const DIAGRAM_SELECTOR = ".printable-diagram"; // Class to identify diagrams
const REPLACEMENT_IMAGE_CLASS = "print-image-replacement"; // Class for generated images

const ExportPDFOption = ({ children, className }: ExportPDFOptionProps) => {
  const { addToast } = useToasts();

  const exportCallback = useCallback(async () => {
    const diagramElements = document.querySelectorAll<HTMLElement>(DIAGRAM_SELECTOR);
    if (diagramElements.length === 0) {
      console.debug("Print: No diagram elements found with selector:", DIAGRAM_SELECTOR);
    }

    console.debug("Preparing diagram elements for printing");

    const originalDisplays: { element: HTMLElement; display: string }[] = [];
    const replacementImages: HTMLImageElement[] = [];

    try {
      await Promise.all(
        Array.from(diagramElements).map(async element => {
          try {
            const dataUrl = await domToImage.toPng(element, {
              bgcolor: imageExportOptionsConfig.bgcolor,
            });
            const img = document.createElement("img");
            img.src = dataUrl;
            img.classList.add(REPLACEMENT_IMAGE_CLASS);
            img.style.width = imageExportOptionsConfig.width;
            img.style.height = imageExportOptionsConfig.height;
            img.style.maxWidth = imageExportOptionsConfig.maxWidth;
            img.style.maxHeight = imageExportOptionsConfig.maxHeight;
            img.style.objectFit = imageExportOptionsConfig.objectFit;
            replacementImages.push(img);
            originalDisplays.push({ element, display: element.style.display });
          } catch (imgError) {
            addToast("error", t("project.dashboard.actions.print.error_generating_image"), "Error");
          }
        }),
      );

      if (originalDisplays.length !== replacementImages.length) {
        addToast("error", t("project.dashboard.actions.print.error_diagram_mismatch"), "Error");
        return;
      }

      for (const [index, { element }] of originalDisplays.entries()) {
        const parentNode = element.parentNode;
        const replacementImage = replacementImages[index];
        if (parentNode && replacementImage) {
          element.style.display = "none";
          parentNode.insertBefore(replacementImage, element.nextSibling);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 100));
      window.print();

      setTimeout(() => {
        for (const img of replacementImages) img.remove();
        for (const { element, display } of originalDisplays) {
          element.style.display = display;
        }
        addToast("success", t("project.dashboard.actions.print.success"), "Success");
      }, 500);
    } catch (error) {
      addToast("error", t("project.dashboard.actions.print.error_processing_diagrams"), "Error");

      for (const img of replacementImages) img.remove();
      for (const { element, display } of originalDisplays) {
        element.style.display = display;
      }
    }
  }, [addToast]);

  return (
    <Option closeOnClick onClick={exportCallback} className={className}>
      {children}
    </Option>
  );
};

export default ExportPDFOption;
